import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { contentService, uploadService } from '../../services/firebaseService';
import { AboutContent, HomeContent } from '../../types';
import RichTextEditor from '../../components/admin/RichTextEditor';

const ContentManager: React.FC = () => {
  // Default content to use if Firebase doesn't have it yet
  const defaultContent: AboutContent = {
    aroundTheWorld: 'Communities are facing rising levels of polarization, stress, and conflict. While many efforts focus on resolving conflict after it has escalated, research across psychology, education, and peace studies consistently shows that peace is most effectively built by cultivating the skills, mindsets, and relationships that prevent conflict in the first place. This is the heart of peace education.',
    peaceEducationIntro: 'Peace education equips people with the ability to understand their own needs, communicate effectively, regulate emotions, and empathize across differences. Studies from UNESCO, the Global Partnership for the Prevention of Armed Conflict, and numerous academic programs have affirmed that peace education contributes to:',
    benefits: [
      'Reduced interpersonal and community violence',
      'Improved emotional resilience and self-regulation',
      'Greater social cohesion and empathy across cultures',
      'Increased capacity for collaboration and conflict transformation'
    ],
    conclusion: 'When people learn concrete tools for connection and compassion, they are better able to navigate differences, de-escalate tensions, and create environments where everyone\'s needs matter. In this way, peace education is not simply a curriculum — it is a strategy for transforming the cultural roots of our collective challenges.'
  };

  const [aboutContent, setAboutContent] = useState<AboutContent>(defaultContent);
  const [homeContent, setHomeContent] = useState<HomeContent>({
    mission: 'To foster compassion and understanding through educational programs and community initiatives that bring people together.',
    vision: 'A world where compassion guides our interactions and creates positive change in communities everywhere.',
    getInvolved: 'Join us in making a difference. Whether through volunteering, donating, or participating in our programs.'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingHome, setSavingHome] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [homeMessage, setHomeMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [benefitInput, setBenefitInput] = useState('');
  const [uploadingStates, setUploadingStates] = useState<Record<string, boolean>>({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const [aboutData, homeData] = await Promise.all([
        contentService.getAboutContent(),
        contentService.getHomeContent()
      ]);
      
      if (aboutData) {
        setAboutContent(aboutData);
      } else {
        setAboutContent(defaultContent);
      }
      
      if (homeData) {
        setHomeContent(homeData);
      }
    } catch (error) {
      console.error('Error loading content:', error);
      setMessage({ type: 'error', text: 'Failed to load content' });
      setAboutContent(defaultContent);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      await contentService.updateAboutContent(aboutContent);
      setMessage({ type: 'success', text: 'About page content saved successfully!' });
    } catch (error: any) {
      console.error('Error saving content:', error);
      setMessage({ type: 'error', text: `Failed to save: ${error.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHome = async () => {
    try {
      setSavingHome(true);
      setHomeMessage(null);
      console.log('💾 Saving home content:', {
        mission: homeContent.mission?.substring(0, 50) + '...',
        missionAudioUrl: homeContent.missionAudioUrl,
        visionAudioUrl: homeContent.visionAudioUrl,
        getInvolvedAudioUrl: homeContent.getInvolvedAudioUrl
      });
      await contentService.updateHomeContent(homeContent);
      console.log('✅ Home content saved successfully!');
      setHomeMessage({ type: 'success', text: 'Home page content saved successfully!' });
    } catch (error: any) {
      console.error('Error saving home content:', error);
      setHomeMessage({ type: 'error', text: `Failed to save: ${error.message}` });
    } finally {
      setSavingHome(false);
    }
  };

  const addBenefit = () => {
    if (benefitInput.trim()) {
      setAboutContent({
        ...aboutContent,
        benefits: [...(aboutContent.benefits || []), benefitInput.trim()]
      });
      setBenefitInput('');
    }
  };

  const removeBenefit = (index: number) => {
    const newBenefits = [...(aboutContent.benefits || [])];
    newBenefits.splice(index, 1);
    setAboutContent({
      ...aboutContent,
      benefits: newBenefits
    });
  };

  const updateBenefit = (index: number, value: string) => {
    const newBenefits = [...(aboutContent.benefits || [])];
    newBenefits[index] = value;
    setAboutContent({
      ...aboutContent,
      benefits: newBenefits
    });
  };

  const handleAudioUpload = async (
    file: File,
    path: string,
    fieldName: string,
    contentType: 'about' | 'home'
  ) => {
    try {
      setUploadingStates(prev => ({ ...prev, [fieldName]: true }));
      setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));
      
      const audioUrl = await uploadService.uploadFile(file, path, (progress) => {
        setUploadProgress(prev => ({ ...prev, [fieldName]: progress }));
      });
      
      setUploadProgress(prev => ({ ...prev, [fieldName]: 100 }));
      
      if (contentType === 'about') {
        setAboutContent({ ...aboutContent, [fieldName]: audioUrl } as AboutContent);
        setMessage({ type: 'success', text: `Audio "${file.name}" uploaded successfully! Don't forget to save.` });
      } else {
        setHomeContent({ ...homeContent, [fieldName]: audioUrl } as HomeContent);
        setHomeMessage({ type: 'success', text: `Audio "${file.name}" uploaded successfully! Don't forget to save.` });
      }
    } catch (error: any) {
      setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));
      if (contentType === 'about') {
        setMessage({ type: 'error', text: `Upload failed: ${error.message}` });
      } else {
        setHomeMessage({ type: 'error', text: `Upload failed: ${error.message}` });
      }
    } finally {
      setUploadingStates(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <div className="admin-content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <div className="admin-sidebar">
        <h3>Admin Panel</h3>
        <nav>
          <ul>
            <li><Link to="/admin">Dashboard</Link></li>
            <li><Link to="/admin/content">Content</Link></li>
            <li><Link to="/admin/board">Board Members</Link></li>
            <li><Link to="/admin/programs">Programs</Link></li>
            <li><Link to="/admin/events">Events</Link></li>
            <li><Link to="/admin/volunteers">Volunteer Applications</Link></li>
            <li><Link to="/admin/contact">Contact Submissions</Link></li>
          </ul>
        </nav>
      </div>
      <div className="admin-content">
        <h1>Content Manager</h1>
        
        <div style={{ marginBottom: '2rem' }}>
          <h2>About Page</h2>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="aboutAudio" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Listen to Our Story Audio File:
            </label>
            {aboutContent.audioUrl && (
              <div style={{ marginBottom: '0.5rem', padding: '0.5rem', background: '#e9ecef', borderRadius: '4px' }}>
                <audio controls src={aboutContent.audioUrl} style={{ width: '100%', maxWidth: '400px' }} />
              </div>
            )}
            <input
              type="file"
              id="aboutAudio"
              accept="audio/*"
              disabled={uploadingStates['audioUrl']}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const path = `audio/about-us/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
                await handleAudioUpload(file, path, 'audioUrl', 'about');
                e.target.value = '';
              }}
              style={{
                width: '100%',
                padding: '0.5rem',
                fontSize: '0.9em',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: uploadingStates['audioUrl'] ? 'wait' : 'pointer',
                opacity: uploadingStates['audioUrl'] ? 0.6 : 1
              }}
            />
            {uploadingStates['audioUrl'] && (
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ width: '100%', height: '4px', background: '#e9ecef', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ 
                    width: `${uploadProgress['audioUrl'] || 0}%`, 
                    height: '100%', 
                    background: '#007bff', 
                    transition: 'width 0.3s ease' 
                  }} />
                </div>
                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '0.25rem' }}>
                  Uploading... {Math.round(uploadProgress['audioUrl'] || 0)}%
                </p>
              </div>
            )}
            {!uploadingStates['audioUrl'] && (
              <p style={{ fontSize: '0.85em', color: '#666', marginTop: '0.25rem' }}>
                Upload an audio file for the "Listen to Our Story" section (optional)
              </p>
            )}
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <RichTextEditor
              value={aboutContent.aroundTheWorld || ''}
              onChange={(value) => setAboutContent({ ...aboutContent, aroundTheWorld: value })}
              placeholder="Enter about page content..."
            />
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '1em',
              fontWeight: 'bold',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? 'Saving...' : 'Save About Page Content'}
          </button>

          {message && (
            <div
              style={{
                marginTop: '1rem',
                padding: '0.75rem',
                borderRadius: '4px',
                backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
                color: message.type === 'success' ? '#155724' : '#721c24',
                fontSize: '0.9em'
              }}
            >
              {message.text}
            </div>
          )}
        </div>

        <div style={{ marginTop: '3rem', marginBottom: '2rem', paddingTop: '2rem', borderTop: '2px solid #ddd' }}>
          <h2>Home Page</h2>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="mission" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Our Mission:
            </label>
            <textarea
              id="mission"
              value={homeContent.mission || ''}
              onChange={(e) => setHomeContent({ ...homeContent, mission: e.target.value })}
              rows={4}
              style={{
                width: '100%',
                padding: '0.5rem',
                fontSize: '0.9em',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontFamily: 'inherit',
                whiteSpace: 'pre-wrap',
                resize: 'vertical'
              }}
              placeholder="To foster compassion and understanding through educational programs..."
            />
            <p style={{ fontSize: '0.85em', color: '#666', marginTop: '0.25rem' }}>
              Tip: Press Enter to create new paragraphs. Line breaks will be preserved.
            </p>
            
            {/* Inline Audio Upload */}
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e9ecef' }}>
              <label htmlFor="missionAudio" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9em', fontWeight: 'bold' }}>
                Audio File (optional):
              </label>
              {homeContent.missionAudioUrl && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <audio controls src={homeContent.missionAudioUrl} style={{ width: '100%', maxWidth: '300px' }} />
                </div>
              )}
              <input
                type="file"
                id="missionAudio"
                accept="audio/*"
                disabled={uploadingStates['missionAudioUrl']}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const path = `audio/home/mission/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
                  await handleAudioUpload(file, path, 'missionAudioUrl', 'home');
                  e.target.value = '';
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '0.85em',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: uploadingStates['missionAudioUrl'] ? 'wait' : 'pointer',
                  opacity: uploadingStates['missionAudioUrl'] ? 0.6 : 1
                }}
              />
              {uploadingStates['missionAudioUrl'] && (
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{ width: '100%', height: '4px', background: '#e9ecef', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ 
                      width: `${uploadProgress['missionAudioUrl'] || 0}%`, 
                      height: '100%', 
                      background: '#007bff', 
                      transition: 'width 0.3s ease' 
                    }} />
                  </div>
                  <p style={{ fontSize: '0.8em', color: '#666', marginTop: '0.25rem' }}>
                    Uploading... {Math.round(uploadProgress['missionAudioUrl'] || 0)}%
                  </p>
                </div>
              )}
              {!uploadingStates['missionAudioUrl'] && (
                <p style={{ fontSize: '0.8em', color: '#666', marginTop: '0.25rem' }}>
                  Upload an audio file for this section (optional)
                </p>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="vision" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Our Vision:
            </label>
            <textarea
              id="vision"
              value={homeContent.vision || ''}
              onChange={(e) => setHomeContent({ ...homeContent, vision: e.target.value })}
              rows={4}
              style={{
                width: '100%',
                padding: '0.5rem',
                fontSize: '0.9em',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontFamily: 'inherit',
                whiteSpace: 'pre-wrap',
                resize: 'vertical'
              }}
              placeholder="A world where compassion guides our interactions..."
            />
            <p style={{ fontSize: '0.85em', color: '#666', marginTop: '0.25rem' }}>
              Tip: Press Enter to create new paragraphs. Line breaks will be preserved.
            </p>
            
            {/* Inline Audio Upload */}
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e9ecef' }}>
              <label htmlFor="visionAudio" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9em', fontWeight: 'bold' }}>
                Audio File (optional):
              </label>
              {homeContent.visionAudioUrl && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <audio controls src={homeContent.visionAudioUrl} style={{ width: '100%', maxWidth: '300px' }} />
                </div>
              )}
              <input
                type="file"
                id="visionAudio"
                accept="audio/*"
                disabled={uploadingStates['visionAudioUrl']}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const path = `audio/home/vision/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
                  await handleAudioUpload(file, path, 'visionAudioUrl', 'home');
                  e.target.value = '';
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '0.85em',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: uploadingStates['visionAudioUrl'] ? 'wait' : 'pointer',
                  opacity: uploadingStates['visionAudioUrl'] ? 0.6 : 1
                }}
              />
              {uploadingStates['visionAudioUrl'] && (
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{ width: '100%', height: '4px', background: '#e9ecef', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ 
                      width: `${uploadProgress['visionAudioUrl'] || 0}%`, 
                      height: '100%', 
                      background: '#007bff', 
                      transition: 'width 0.3s ease' 
                    }} />
                  </div>
                  <p style={{ fontSize: '0.8em', color: '#666', marginTop: '0.25rem' }}>
                    Uploading... {Math.round(uploadProgress['visionAudioUrl'] || 0)}%
                  </p>
                </div>
              )}
              {!uploadingStates['visionAudioUrl'] && (
                <p style={{ fontSize: '0.8em', color: '#666', marginTop: '0.25rem' }}>
                  Upload an audio file for this section (optional)
                </p>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="getInvolved" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Get Involved:
            </label>
            <textarea
              id="getInvolved"
              value={homeContent.getInvolved || ''}
              onChange={(e) => setHomeContent({ ...homeContent, getInvolved: e.target.value })}
              rows={4}
              style={{
                width: '100%',
                padding: '0.5rem',
                fontSize: '0.9em',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontFamily: 'inherit',
                whiteSpace: 'pre-wrap',
                resize: 'vertical'
              }}
              placeholder="Join us in making a difference. Whether through volunteering..."
            />
            <p style={{ fontSize: '0.85em', color: '#666', marginTop: '0.25rem' }}>
              Tip: Press Enter to create new paragraphs. Line breaks will be preserved.
            </p>
            
            {/* Inline Audio Upload */}
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e9ecef' }}>
              <label htmlFor="getInvolvedAudio" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9em', fontWeight: 'bold' }}>
                Audio File (optional):
              </label>
              {homeContent.getInvolvedAudioUrl && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <audio controls src={homeContent.getInvolvedAudioUrl} style={{ width: '100%', maxWidth: '300px' }} />
                </div>
              )}
              <input
                type="file"
                id="getInvolvedAudio"
                accept="audio/*"
                disabled={uploadingStates['getInvolvedAudioUrl']}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const path = `audio/home/get-involved/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
                  await handleAudioUpload(file, path, 'getInvolvedAudioUrl', 'home');
                  e.target.value = '';
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '0.85em',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: uploadingStates['getInvolvedAudioUrl'] ? 'wait' : 'pointer',
                  opacity: uploadingStates['getInvolvedAudioUrl'] ? 0.6 : 1
                }}
              />
              {uploadingStates['getInvolvedAudioUrl'] && (
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{ width: '100%', height: '4px', background: '#e9ecef', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ 
                      width: `${uploadProgress['getInvolvedAudioUrl'] || 0}%`, 
                      height: '100%', 
                      background: '#007bff', 
                      transition: 'width 0.3s ease' 
                    }} />
                  </div>
                  <p style={{ fontSize: '0.8em', color: '#666', marginTop: '0.25rem' }}>
                    Uploading... {Math.round(uploadProgress['getInvolvedAudioUrl'] || 0)}%
                  </p>
                </div>
              )}
              {!uploadingStates['getInvolvedAudioUrl'] && (
                <p style={{ fontSize: '0.8em', color: '#666', marginTop: '0.25rem' }}>
                  Upload an audio file for this section (optional)
                </p>
              )}
            </div>
          </div>


          <button
            onClick={handleSaveHome}
            disabled={savingHome}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '1em',
              fontWeight: 'bold',
              cursor: savingHome ? 'not-allowed' : 'pointer',
              opacity: savingHome ? 0.6 : 1
            }}
          >
            {savingHome ? 'Saving...' : 'Save Home Page Content'}
          </button>

          {homeMessage && (
            <div
              style={{
                marginTop: '1rem',
                padding: '0.75rem',
                borderRadius: '4px',
                backgroundColor: homeMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                color: homeMessage.type === 'success' ? '#155724' : '#721c24',
                fontSize: '0.9em'
              }}
            >
              {homeMessage.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentManager;
